import { useMemo, useCallback, useState } from "react";
import { useAuthenticatedPracticesApi } from "@/api/practices-api";
import { useAuthenticatedProfessionalsApi } from "@/api/professionals-api";
import { useAuthenticatedUsersApi } from "@/api/users-api";
import { DEFAULT_PAGE_SIZE } from "@/constants";
import { useCurrentUserFromContext } from "@/context/user-context";
import {
  mapNotificationItemsToUi,
  type NotificationAudience,
  type UiNotification
} from "@/utilities/notification-view";
import { getErrorMessage } from "@/utilities/api-utils";

export function useRoleNotifications(options?: {
  page?: number;
  pageSize?: number;
  isRead?: boolean;
  searchTerm?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}) {
  const user = useCurrentUserFromContext();
  const userId = user?.id ?? "";
  const practiceId = user?.practice?.id;
  const professionalId = user?.professional?.id;
  const isPractice = user?.userType === "practice";
  const isProfessional = user?.userType === "professional";
  const isUserInbox =
    user?.userType === "admin" || user?.userType === "support";
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? DEFAULT_PAGE_SIZE;
  const isRead = options?.isRead;
  const searchTerm = options?.searchTerm;
  const sortBy = options?.sortBy;
  const sortOrder = options?.sortOrder;

  const { useGetPracticeNotificationsQuery } = useAuthenticatedPracticesApi();
  const { useGetProfessionalNotificationsQuery } =
    useAuthenticatedProfessionalsApi();
  const {
    useGetUserNotificationsQuery,
    useMarkNotificationReadMutation,
    useMarkAllNotificationsReadMutation
  } = useAuthenticatedUsersApi();

  const queryArgs = {
    page,
    pageSize,
    isRead,
    searchTerm,
    sortBy,
    sortOrder
  };

  const practiceNotifications = useGetPracticeNotificationsQuery(
    { entityId: practiceId ?? "", ...queryArgs },
    { skip: !isPractice || !practiceId }
  );

  const professionalNotifications = useGetProfessionalNotificationsQuery(
    { entityId: professionalId ?? "", ...queryArgs },
    { skip: !isProfessional || !professionalId }
  );

  const userNotifications = useGetUserNotificationsQuery(
    { entityId: userId, ...queryArgs },
    { skip: !isUserInbox || !userId }
  );

  const [markRead] = useMarkNotificationReadMutation();
  const [markAllRead] = useMarkAllNotificationsReadMutation();
  const [pendingReadIds, setPendingReadIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const [pendingMarkAll, setPendingMarkAll] = useState(false);

  const raw = isPractice
    ? practiceNotifications.data
    : isProfessional
      ? professionalNotifications.data
      : isUserInbox
        ? userNotifications.data
        : undefined;

  // Drop optimistic overlays once the server reports an empty inbox.
  // Adjust during render (not in an effect) so we avoid a cascading render.
  if ((raw?.unreadCount ?? 0) === 0) {
    if (pendingMarkAll) {
      setPendingMarkAll(false);
    }
    if (pendingReadIds.size > 0) {
      setPendingReadIds(new Set());
    }
  }

  const isLoading = isPractice
    ? practiceNotifications.isLoading
    : isProfessional
      ? professionalNotifications.isLoading
      : isUserInbox
        ? userNotifications.isLoading
        : false;

  const isError = isPractice
    ? practiceNotifications.isError
    : isProfessional
      ? professionalNotifications.isError
      : isUserInbox
        ? userNotifications.isError
        : false;

  const error = isError
    ? getErrorMessage(
        isPractice
          ? practiceNotifications.error
          : isProfessional
            ? professionalNotifications.error
            : userNotifications.error
      )
    : null;

  const audience: NotificationAudience | undefined = isPractice
    ? "practice"
    : isProfessional
      ? "professional"
      : user?.userType === "admin" || user?.userType === "support"
        ? "admin"
        : undefined;

  const notifications: UiNotification[] = useMemo(() => {
    if (!audience) {
      return [];
    }

    const mapped = mapNotificationItemsToUi(raw?.items ?? [], audience).map(
      (item) => {
        if (pendingMarkAll || pendingReadIds.has(item.id)) {
          return { ...item, read: true };
        }

        return item;
      }
    );

    if (isRead === false) {
      return mapped.filter((item) => !item.read);
    }

    return mapped;
  }, [raw?.items, audience, pendingMarkAll, pendingReadIds, isRead]);

  const unreadCount = pendingMarkAll
    ? 0
    : Math.max(
        0,
        (raw?.unreadCount ?? 0) -
          (raw?.items ?? []).filter(
            (item) => !item.readAt && pendingReadIds.has(item.notificationId)
          ).length
      );
  const totalCount = raw?.pagination?.totalCount ?? 0;

  const invalidateContext = useMemo(() => {
    return isPractice && practiceId
      ? { kind: "practice" as const, entityId: practiceId }
      : isProfessional && professionalId
        ? { kind: "professional" as const, entityId: professionalId }
        : isUserInbox && userId
          ? { kind: "user" as const, entityId: userId }
          : undefined;
  }, [isPractice, practiceId, isProfessional, professionalId, isUserInbox, userId]);

  const markNotificationRead = useCallback(
    async (notificationId: string) => {
      if (!userId) return;
      setPendingReadIds((current) => new Set(current).add(notificationId));
      try {
        await markRead({
          userId,
          notificationId,
          invalidateContext
        }).unwrap();
      } catch (caught) {
        setPendingReadIds((current) => {
          const next = new Set(current);
          next.delete(notificationId);
          return next;
        });
        throw caught;
      }
    },
    [markRead, userId, invalidateContext]
  );

  const markAllNotificationsRead = useCallback(async () => {
    if (!userId) return;
    setPendingMarkAll(true);
    try {
      await markAllRead({ userId, invalidateContext }).unwrap();
    } catch (caught) {
      setPendingMarkAll(false);
      throw caught;
    }
  }, [markAllRead, userId, invalidateContext]);

  return {
    notifications,
    unreadCount,
    totalCount,
    isLoading,
    isError,
    error,
    supportsApi:
      Boolean(isPractice && practiceId) ||
      Boolean(isProfessional && professionalId) ||
      Boolean(isUserInbox && userId),
    markNotificationRead,
    markAllNotificationsRead
  };
}
